<template>
  <material-card class="mx-4">
    <v-layout slot="header" px-3>
      <v-flex my-4 pl-4 title align-center justify-center>Karty mieszkańców</v-flex>

      <v-spacer />

      <v-flex>
        <search />
      </v-flex>
    </v-layout>

    <v-layout>
      <v-flex xs6>
        <div v-if="selectable">
          <check-permission v-if="!!nextStepButtonPermission" :permissions="nextStepButtonPermission">
            <v-btn
              color="primary"
              :disabled="nextState == 15 && (!selected || selected.length == 0)"
              @click="openModal('confirm')"
            >
              <v-icon>mdi-check mr-2</v-icon>

              Przekaż karty dalej
            </v-btn>
          </check-permission>

          <check-permission v-if="$route.params.state === CardState[CardState.PREPARED]" permissions="CARD.TRANSPORT">
            <v-btn @click="preparePrint">
              Wydrukuj potwierdzenie odbioru
            </v-btn>
          </check-permission>

          <check-permission v-if="$route.params.state === CardState[CardState.PRINTED]" permissions="CARD.REPRINT">
            <v-btn color="warning" :disabled="!selected || selected.length == 0" @click="openModal('reprint')">
              <v-icon>mdi-printer mr-2</v-icon>
              Cofnij do ponownego wydruku
            </v-btn>
          </check-permission>
        </div>

        <check-permission permissions="CARD.EXTEND_EXPIRATION">
          <v-btn color="primary" @click="openModal('senior')">
            Ustaw karty seniorów jako bezterminowe
          </v-btn>
        </check-permission>
      </v-flex>

      <v-flex text-xs-right>
        <v-layout>
          <v-flex xs8>
            <v-select
              v-model="style"
              item-text="text"
              label="Styl karty"
              item-value="id"
              clearable
              :items="styleList"
              @change="styleSelected"
            />
          </v-flex>
          <v-flex xs8>
            <v-select
              v-model="collectionPointId"
              item-text="name"
              label="Punkt odbioru karty"
              item-value="id"
              clearable
              :items="collectionPoints"
              @change="collectionPointSelected"
            />
          </v-flex>

          <v-flex>
            <check-permission permissions="CARD.CSV">
              <post-form text="Pobierz CSV" :url="csvUrl" :fields="csvFilters" />
            </check-permission>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-layout>
      <v-flex>
        <v-tabs v-model="activeTab">
          <check-permission v-for="tab in table.tabs" :key="tab.route.params.state" :permissions="tab.permission">
            <v-tab exact replace :to="route(tab)">
              {{ tab.text }}
            </v-tab>
          </check-permission>
        </v-tabs>
      </v-flex>
    </v-layout>

    <material-table
      :actions="actions"
      :headers="headers"
      :items="list"
      :selectable="selectable"
      :total-count="count"
      :rows-per-page="25"
      @details="goToProfile"
      @selected="setSelected"
    />

    <confirm-modal
      :description="confirmDescription"
      :title="confirmTitle"
      :show="modals.confirm"
      @close="closeModal('confirm')"
      @confirm="setState"
    />

    <confirm-modal
      description="Czy na pewno chcesz ustawić wszystkie karty seniorów jako bezterminowe?"
      title="Potwierdzenie akcji"
      :show="modals.senior"
      @close="closeModal('senior')"
      @confirm="setSeniorCardsUnlimited"
    />

    <confirm-modal
      description="Kliknięcie Tak spowoduje cofnięcie wybranych kart do statusu przygotowanych do wydruku w celu ponownego wydruku."
      title="Czy na pewno cofnąć karty do ponownego wydruku?"
      :show="modals.reprint"
      @close="closeModal('reprint')"
      @confirm="reprint"
    />

    <div v-if="mountRenderPrintComponent" v-show="false">
      <dispensed-to-transport ref="confirmHTML" :cards="cardsToConfirm" @rendered="openRenderedView" />
    </div>
  </material-card>
</template>

<script>
import { CardState, CardStateTranslation, dateFilter } from '@witkac/kartamiejska-shared';
import { mapState } from 'vuex';

import ConfirmModal from '@/components/helpers/ConfirmModal';
import MaterialCard from '@/components/material/Card';
import MaterialTable from '@/components/material/Table';
import Search from '@/components/material/table/Search';
import PostForm from '@/components/postForm/PostForm';
import DispensedToTransport from '@/components/printable/DispensedToTransport';
import { displayModalMixin } from '@/mixins/displayModal';
import { listMixin } from '@/mixins/list';
import { DETAILS_ACTION } from '@/utils/actions';
import { config } from '@/utils/config';
import { tryParseInt } from '@witkac/utils/tryParseInt';
import { getCardStatePermission } from './cardStatePermissions';

const errors = {
  listError: 'Wystąpił błąd podczas pobierania listy kart.',
  getError: 'Wystąpił błąd podczas pobierania danych kart.',
  deleted: 'Usunięto kartę.',
  deleteError: 'Nie udało się usunąć karty.'
};

const statesWithoutNumber = [CardState.NOT_CONFIRMED];

const statesWithEndDate = [CardState.ACTIVE, CardState.READY];

const statesWithCollectionPoint = [
  CardState.PRINTED,
  CardState.PREPARED,
  CardState.TRANSPORT,
  CardState.ORDERED,
  CardState.PREPARED_TO_PRINT
];

// @vuese
// Lista wniosków
// @group card
export default {
  components: {
    ConfirmModal,
    MaterialCard,
    MaterialTable,
    Search,
    DispensedToTransport,
    PostForm
  },
  mixins: [displayModalMixin, listMixin('card', errors)],
  data() {
    return {
      collectionPointId: tryParseInt(this.$route.query.collectionPointId),
      style: this.$route.query.style || '',
      styleList: [
        { id: '', text: 'Wszystkie' },
        { id: 'LOGO', text: 'Logo' },
        { id: 'RATUSZ', text: 'Ratusz' }
      ],
      csvUrl: config.csvURL + '/card',
      CardState,
      table: {
        tabs: [
          {
            permission: getCardStatePermission(CardState[CardState.NOT_CONFIRMED]),
            text: CardStateTranslation[CardState.NOT_CONFIRMED],
            route: { name: 'cardsByState', params: { state: CardState[CardState.NOT_CONFIRMED] } },
            value: 'NOT_CONFIRMED'
          },
          {
            permission: getCardStatePermission(CardState[CardState.ORDERED]),
            text: CardStateTranslation[CardState.ORDERED],
            route: { name: 'cardsByState', params: { state: CardState[CardState.ORDERED] } }
          },
          {
            permission: getCardStatePermission(CardState[CardState.PREPARED_TO_PRINT]),
            text: CardStateTranslation[CardState.PREPARED_TO_PRINT],
            route: { name: 'cardsByState', params: { state: CardState[CardState.PREPARED_TO_PRINT] } }
          },
          {
            permission: getCardStatePermission(CardState[CardState.PRINTED]),
            text: CardStateTranslation[CardState.PRINTED],
            route: { name: 'cardsByState', params: { state: CardState[CardState.PRINTED] } }
          },
          {
            permission: getCardStatePermission(CardState[CardState.PREPARED]),
            text: CardStateTranslation[CardState.PREPARED],
            route: { name: 'cardsByState', params: { state: CardState[CardState.PREPARED] } }
          },
          {
            permission: getCardStatePermission(CardState[CardState.TRANSPORT]),
            text: CardStateTranslation[CardState.TRANSPORT],
            route: { name: 'cardsByState', params: { state: CardState[CardState.TRANSPORT] } }
          },
          {
            permission: getCardStatePermission(CardState[CardState.READY]),
            text: CardStateTranslation[CardState.READY],
            route: { name: 'cardsByState', params: { state: CardState[CardState.READY] } }
          },
          {
            permission: getCardStatePermission(CardState[CardState.ACTIVE]),
            text: CardStateTranslation[CardState.ACTIVE],
            route: { name: 'cardsByState', params: { state: CardState[CardState.ACTIVE] } }
          },
          {
            permission: getCardStatePermission(CardState[CardState.INACTIVE]),
            text: CardStateTranslation[CardState.INACTIVE],
            route: { name: 'cardsByState', params: { state: CardState[CardState.INACTIVE] } }
          }
        ]
      },
      actions: [{ ...DETAILS_ACTION, permissions: ['PROFILE.GET'], entityIdPath: 'profile.userId' }],
      activeTab: this.$route.params.state,
      cardsToConfirm: [],
      mountRenderPrintComponent: false,
      selected: []
    };
  },
  computed: {
    headers() {
      const headers = [
        { text: 'ID', value: 'id', orderBy: 'id', link: false },
        {
          text: 'Nazwisko, imię',
          sortable: false,
          link: false,
          value: 'profile.lastName',
          render: item => item.profile.lastName + ' ' + item.profile.firstName
        },
        {
          text: 'Styl',
          sortable: false,
          link: false,
          value: 'style',
          render: item => item.style
        }
      ];
      if (this.$route.params.state && !statesWithoutNumber.includes(this.currentEnumState)) {
        headers.push({ text: 'Numer karty', orderBy: 'number', value: 'number', link: false });
      }
      if (this.$route.params.state && statesWithCollectionPoint.includes(this.currentEnumState)) {
        headers.push({
          text: 'Punkt odbioru',
          sortable: false,
          render: item => item.collectionPoint && item.collectionPoint.name
        });
      }
      headers.push({
        text: 'Data utworzenia',
        orderBy: 'created',
        value: 'created',
        render: item => dateFilter(item.created)
      });
      if (this.$route.params.state && statesWithEndDate.includes(this.currentEnumState)) {
        headers.push({
          text: 'Data ważności karty',
          orderBy: 'endDate',
          value: 'endDate',
          render: item => (item.endDate ? dateFilter(item.endDate) : 'Bezterminowa')
        });
      }
      return headers;
    },
    selectable() {
      const selectableStates = [CardState.PRINTED, CardState.PREPARED, CardState.TRANSPORT, CardState.ORDERED];
      return !!this.count && this.$route.params.state && selectableStates.includes(this.currentEnumState);
    },
    nextState() {
      switch (this.currentEnumState) {
        case CardState.ORDERED:
          return CardState.PREPARED_TO_PRINT;

        case CardState.PRINTED:
          return CardState.PREPARED;

        case CardState.PREPARED:
          return CardState.TRANSPORT;

        case CardState.TRANSPORT:
          return CardState.READY;

        default:
          return undefined;
      }
    },
    confirmDescription() {
      switch (this.nextState) {
        case CardState.PREPARED_TO_PRINT:
          return 'Kliknięcie Tak spowoduje ustawienie zamówionym kartom statusu przygotowanych do druku.';

        case CardState.PREPARED:
          return 'Kliknięcie Tak spowoduje ustawienie wydrukowanym kartom statusu przygotowanych do odbioru przez operatora.';

        case CardState.TRANSPORT:
          return 'Upewnij się, że zostało wydrukowane i podpisane potwierdzenie odbioru kart. Kliknięcie Tak spowoduje ustawienie przygotowanym kartom statusu przekazanie do punktu odbioru.';

        case CardState.READY:
          return 'Kliknięcie Tak spowoduje ustawienie transportowanym kartom statusu gotowych do odbioru.';

        default:
          return '';
      }
    },
    confirmTitle() {
      switch (this.nextState) {
        case CardState.PREPARED_TO_PRINT:
          return `Czy na pewno przygotowano ${this.allOrSelected} karty do druku?`;

        case CardState.PREPARED:
          return `Czy na pewno przygotowano ${this.allOrSelected} karty do odbioru?`;

        case CardState.TRANSPORT:
          return `Czy na pewno wydano operatorowi ${this.allOrSelected} karty?`;

        case CardState.READY:
          return `Czy na pewno dostarczono ${this.allOrSelected} karty?`;

        default:
          return '';
      }
    },
    allOrSelected() {
      if (this.count && (!this.selected.length || this.selected.length === this.count)) {
        return 'wszystkie';
      } else if (this.count && this.selected.length) {
        return 'wybrane';
      }
    },
    nextStepButtonPermission() {
      switch (this.$route.params.state) {
        case CardState[CardState.ORDERED]:
          return 'CARD.PREPARE_TO_PRINT';
        case CardState[CardState.PRINTED]:
          return 'CARD.PREPARED';
        case CardState[CardState.PREPARED]:
          return 'CARD.TRANSPORT';
        case CardState[CardState.TRANSPORT]:
          return 'CARD.DELIVERED';
        default:
          return undefined;
      }
    },
    currentEnumState() {
      return CardState[this.$route.params.state];
    },
    ...mapState({
      collectionPoints: state => state.collectionPoint.list
    })
  },
  methods: {
    // @vuese
    // Otwiera okienko z potwierdzeniem usługi
    // @arg `id` - identyfikator usługi
    goToProfile(card) {
      const id = card.profile.userId;
      this.$router.push({ name: 'client', params: { id } });
    },
    // @vuese
    // Konfiguracja filtrów (dodanie state)
    filters(pagination, route) {
      return { ...pagination, state: route.params.state, collectionPointId: this.collectionPointId, style: this.style };
    },

    // @vuese
    // Routing zakładki
    route(tab) {
      return { ...tab.route, query: { ...this.$route.query, page: 1 } };
    },

    // @vuese
    // Ustawia kartom status gotowe do odebrania
    cardsReady() {
      return this.$store.dispatch('card/cardsReady', this.selected);
    },
    // @vuese
    // Zmiana statusu kart
    setState() {
      const nextStateString = CardState[this.nextState];
      const subaction = nextStateString
        .split('_')
        .map(substring => substring.charAt(0) + substring.slice(1).toLowerCase())
        .join('');

      this.$store
        .dispatch('card/cards' + subaction, this.selected)
        .then(() => {
          this.closeModal('confirm');
          this.$router.push(this.route({ route: { name: 'cardsByState', params: { state: nextStateString } } }));
        })
        .catch(err => this.notifyError('Nie udało się zaktualizować kart.'));
    },
    // @vuese
    // Ustawienie zaznaczonych kart
    setSelected(selected) {
      this.selected = selected;
    },
    // @vuese
    // Cofnięcie kart do ponownego wydruku
    reprint() {
      this.$store
        .dispatch('card/cardsReprint', this.selected)
        .then(() => {
          this.closeModal('reprint');
          this.$router.push(
            this.route({
              route: { name: 'cardsByState', params: { state: CardState[CardState.PREPARED_TO_PRINT] } }
            })
          );
        })
        .catch(err => this.notifyError('Nie udało się cofnąć kart do ponownego wydruku.'));
    },
    // @vuese
    // Pobranie listy kart do potwierdzenia
    async preparePrint() {
      this.mountRenderPrintComponent = true;
      const filters = { state: CardState[this.currentEnumState], count: -1 };
      if (this.selected && this.selected.length) {
        filters.ids = this.selected;
      }
      this.cardsToConfirm = await this.$store.dispatch('card/customList', {
        filters,
        fields: { number: true, collectionPoint: { name: true }, profile: { firstName: true, lastName: true } }
      });
    },
    // @vuese
    // Otworzenie wyrenderowanego widoku w nowym oknie
    openRenderedView() {
      const ref = this.$refs.confirmHTML.$el;
      const newWindow = window.open();
      newWindow.document.body.appendChild(ref);

      this.$nextTick(() => {
        this.mountRenderPrintComponent = false;
        this.cardsToConfirm = [];
      });
    },
    // @vuese
    // Wybranie punktu odbioru karty
    collectionPointSelected(value) {
      this.$router.replace({
        ...this.$route,
        query: { ...this.$route.query, collectionPointId: value, page: 1 }
      });
    },
    styleSelected(value) {
      this.$router.replace({
        ...this.$route,
        query: { ...this.$route.query, style: value, page: 1 }
      });
    },
    // @vuese
    // Ustawienie kart seniorów jako bezterminowych
    setSeniorCardsUnlimited() {
      this.$store
        .dispatch('card/seniorCardSetAsUnlimited')
        .then(response => {
          this.closeModal('senior');
          const updatedCount = response.ids.length;
          const hasMore = response.hasMore;
          const message =
            updatedCount === 0
              ? 'Nie zaktualizowano żadnych kart seniorów.'
              : `Zaktualizowano ${updatedCount} kart seniorów.${
                  hasMore ? '\nNadal pozostały karty do aktualizacji.' : '\nWszystkie karty zostały zaktualizowane.'
                }`;
          this.notifySuccess(message);
          this.reloadList(this.$route);
        })
        .catch(() => this.notifyError('Nie udało się zaktualizować kart seniorów.'));
    }
  }
};
</script>
